import {Product, User} from '@hconnect/apiclient'
import {Button, Modal, ModalContent, ModalFooter, ModalTitle, TextRegular} from '@hm/fundamental'
import React, {useState} from 'react'

import {useTranslation} from '../../../hooks/useTranslation'
import {Permission} from '../../../modules/LoggedInUser'
import {RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {useInvitationProducts} from '../hooks/useInvitationProducts'
import {useInviteUserToProducts} from '../hooks/useInviteUserToProducts'
import {InviteUserButton} from '../InviteUserButton'

type InvitationDialogProps = {
  open: boolean
  setOpenModal: (open: boolean) => void
  products: Product[]
  'data-test-id': string
  onClose: () => void
  user?: User | null
  refetchUser?: () => void
  loggedInUserPermissions?: Permission[] | null
  roles?: RoleAssignment[]
  secondActionText?: string
  secondAction?: () => void
}

export const InvitationDialog = ({
  open,
  setOpenModal,
  products,
  refetchUser,
  loggedInUserPermissions,
  roles,
  user,
  'data-test-id': dataTestId,
  onClose,
  secondActionText,
  secondAction
}: InvitationDialogProps) => {
  const {t} = useTranslation()
  const [isInviting, setIsInviting] = useState(false)
  const [invitationProducts, setInvitationProducts] = useState<Product[]>(
    user?.invitationProducts?.map((invitationProduct) => invitationProduct.product) || []
  )

  const {filteredProducts} = useInvitationProducts(products, loggedInUserPermissions, roles)
  const {mutate: inviteToProducts, isLoading: isInvitingToProducts} = useInviteUserToProducts(
    user,
    refetchUser
  )

  const handleInvite = () => {
    if (user && invitationProducts.length > 0) {
      inviteToProducts(
        {user, products: invitationProducts},
        {
          onSuccess: () => {
            setInvitationProducts([])
            onClose()
          }
        }
      )
    }
  }

  return (
    <Modal open={open} onClose={() => setOpenModal(false)} data-test-id="invitation-dialog">
      <ModalTitle>{t('manageUser.invitations')}</ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-2">
          <TextRegular>{t('manageUser.inviteUserToProducts')}</TextRegular>
          {filteredProducts.map((product, key) => (
            <InviteUserButton
              data-test-id={dataTestId}
              key={key}
              product={product}
              user={user}
              refetchUser={refetchUser}
              isInviting={isInviting}
              setIsInviting={setIsInviting}
              showNewUserProfile={true}
              invitationProducts={invitationProducts}
              setInvitationProducts={setInvitationProducts}
            />
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        {secondAction && secondActionText && (
          <Button
            size="large"
            variant="tertiary"
            onClick={() => secondAction()}
            data-test-id="button-invite-second-action"
            type="button"
          >
            {secondActionText}
          </Button>
        )}
        <Button
          size="large"
          onClick={handleInvite}
          data-test-id="button-invite-user-confirm"
          type="button"
          disabled={isInvitingToProducts || invitationProducts.length === 0}
        >
          {t('manageUser.invite')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
